import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { PdfPreview } from "@/components/m3/PdfPreview";
import { RigeneraDialog } from "@/components/m3/RigeneraDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3DocCompletamento,
  useM3DocumentiPacchetto,
  useM3SalvaDocCompletamento,
} from "@/hooks/useM3";
import { apiDownloadFile } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { DocChip, PaccoDetail, SorgenteInfo } from "@/types/api";

const SORGENTI_META = [
  { key: "ndp" as const, label: "NDP" },
  { key: "ddt" as const, label: "DDT" },
  { key: "fattura" as const, label: "Fattura" },
];

function SorgenteCard({ label, info }: { label: string; info: SorgenteInfo }) {
  const ok = info.stato === "agganciata";
  return (
    <div
      className={cn(
        "rounded-lg border p-3",
        ok ? "border-emerald-200 bg-emerald-50" : "border-border bg-card",
      )}
    >
      <div className="flex items-center gap-1.5 text-[12.5px] font-bold">
        {ok ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        ) : (
          <Clock className="h-3.5 w-3.5 text-amber-600" />
        )}
        {label}
      </div>
      <div className={cn("mt-1.5 text-[11px]", ok ? "text-muted-foreground" : "text-amber-700")}>
        {ok ? info.descrizione || info.riferimento || "agganciata" : info.cosa_manca || "mancante"}
      </div>
    </div>
  );
}

function TplBadge({ ultima }: { ultima: number | null }) {
  return (
    <span
      title={`Template aggiornato disponibile${ultima ? ` (v${ultima})` : ""}`}
      className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700"
    >
      tpl{ultima ? ` v${ultima}` : ""} ▲
    </span>
  );
}

// ── Livello 1: panoramica ──────────────────────────────────────────────

function Livello1({
  pacco,
  onCompleta,
  onRigeneraDoc,
  onAnteprima,
}: {
  pacco: PaccoDetail;
  onCompleta: (tipo: string) => void;
  onRigeneraDoc: (tipo: string, label: string) => void;
  onAnteprima: (fileId: string, label: string) => void;
}) {
  const scaricaDoc = (fileId: string) =>
    apiDownloadFile(`/api/v1/m3/documenti/${fileId}/scarica`);

  return (
    <div className="flex-1 space-y-6 overflow-y-auto p-5">
      <section>
        <h4 className="mb-2.5 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
          Sorgenti agganciate
        </h4>
        <div className="grid grid-cols-3 gap-2.5">
          {pacco.sorgenti &&
            SORGENTI_META.map((s) => (
              <SorgenteCard key={s.key} label={s.label} info={pacco.sorgenti![s.key]} />
            ))}
        </div>
      </section>

      <section>
        <h4 className="mb-2.5 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
          Documenti del pacchetto · {pacco.documenti.length}
        </h4>
        <div className="space-y-2">
          {pacco.documenti.map((d) => (
            <DocRow
              key={d.tipo}
              doc={d}
              onCompleta={() => onCompleta(d.tipo)}
              onRigenera={() => onRigeneraDoc(d.tipo, d.label)}
              onScarica={() => d.file_id && scaricaDoc(d.file_id)}
              onAnteprima={() => d.file_id && onAnteprima(d.file_id, d.label)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function DocRow({
  doc,
  onCompleta,
  onRigenera,
  onScarica,
  onAnteprima,
}: {
  doc: DocChip;
  onCompleta: () => void;
  onRigenera: () => void;
  onScarica: () => void;
  onAnteprima: () => void;
}) {
  const mancaTpl = doc.template_mancante;
  const daCompletare = doc.stato === "da_completare";
  const daGenerare = doc.stato === "da_generare";  // ha template ma non ancora generato
  const generato = doc.provenienza === "generato" && doc.stato_doc !== "mancante";
  // Documento con campi manuali ormai tutti compilati: «Completato» + «Modifica».
  const completato = doc.completabile && !mancaTpl && doc.stato === "generato";
  const attesaDdt = daGenerare && doc.stadio_generazione === "da_ddt";
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3",
        mancaTpl
          ? "border-amber-200 bg-amber-50/40"
          : daCompletare
            ? "border-amber-200 bg-amber-50/50"
            : "border-border bg-card",
      )}
    >
      <div
        className={cn(
          "flex h-8 w-8 flex-none items-center justify-center rounded-md",
          daCompletare || mancaTpl ? "bg-amber-100 text-amber-700" : "bg-brand-50 text-brand-700",
        )}
      >
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-semibold">{doc.label}</span>
          {doc.template_update && <TplBadge ultima={doc.template_versione_ultima} />}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {mancaTpl
            ? "carica il template in Impostazioni → Template"
            : attesaDdt
              ? "si genera con il DDT"
              : generato
                ? "generato"
                : doc.provenienza === "ricevuto"
                  ? "ricevuto"
                  : "da generare"}
          {doc.lingue?.length ? ` · ${doc.lingue.join(", ").toUpperCase()}` : ""}
        </div>
      </div>
      <span
        className={cn(
          "flex-none rounded-full px-2 py-0.5 text-[10.5px] font-semibold",
          mancaTpl
            ? "bg-amber-100 text-amber-800"
            : daCompletare
              ? "bg-amber-100 text-amber-800"
              : generato
                ? "bg-emerald-100 text-emerald-800"
                : "bg-muted text-muted-foreground",
        )}
      >
        {mancaTpl
          ? "Manca template"
          : daCompletare
            ? `Da completare · ${doc.campi_mancanti}`
            : completato
              ? "Completato"
              : generato
                ? "Generato"
                : attesaDdt
                  ? "Attende DDT"
                  : "Da generare"}
      </span>
      {doc.completabile && !mancaTpl && !daGenerare && (
        <button
          onClick={onCompleta}
          className={cn(
            "flex-none rounded-md border px-2.5 py-1 text-[12px] font-semibold",
            daCompletare
              ? "border-brand-200 bg-brand-50/60 text-brand-700 hover:bg-brand-50"
              : "border-border text-muted-foreground hover:bg-muted hover:text-brand-700",
          )}
        >
          {daCompletare ? "Completa" : "Modifica"}
        </button>
      )}
      {generato && (
        <button
          onClick={onRigenera}
          title="Rigenera documento"
          className="flex-none rounded-md border border-border p-1.5 text-muted-foreground hover:text-brand-700"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
      {doc.file_id && (
        <button
          onClick={onAnteprima}
          title="Anteprima"
          className="flex-none rounded-md border border-border p-1.5 text-muted-foreground hover:text-brand-700"
        >
          <Eye className="h-3.5 w-3.5" />
        </button>
      )}
      {doc.file_id && (
        <button
          onClick={onScarica}
          title="Scarica"
          className="flex-none rounded-md border border-border p-1.5 text-muted-foreground hover:text-brand-700"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Livello 2: completamento documento ─────────────────────────────────

const GRUPPO_LABEL: Record<string, string> = {
  automatico: "Automatici — da NDP",
  in_attesa: "In attesa di una sorgente",
  manuale: "Da completare a mano",
};

function Livello2({ caricoId, tipo }: { caricoId: string; tipo: string }) {
  const { data, isLoading } = useM3DocCompletamento(caricoId, tipo);
  const salva = useM3SalvaDocCompletamento(caricoId, tipo);
  const [valori, setValori] = useState<Record<string, string>>({});
  const initedFor = useRef<string | null>(null);

  useEffect(() => {
    const key = `${caricoId}:${tipo}`;
    if (!data || initedFor.current === key) return;
    initedFor.current = key;
    const init: Record<string, string> = {};
    for (const c of data.campi) init[c.chiave] = c.valore;
    setValori(init);
  }, [data, caricoId, tipo]);

  const gruppi: Array<"automatico" | "in_attesa" | "manuale"> = [
    "automatico",
    "in_attesa",
    "manuale",
  ];

  return (
    <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden p-5 md:grid-cols-[1fr_320px]">
      <div className="min-h-[40vh] overflow-hidden rounded-lg border border-border bg-muted">
        <PdfPreview fileId={data?.file_id ?? null} />
      </div>
      <div className="flex min-h-0 flex-col">
        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
          {isLoading || !data ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : data.campi.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Nessun campo da completare.
            </div>
          ) : (
            gruppi.map((g) => {
              const campi = data.campi.filter((c) => c.gruppo === g);
              if (!campi.length) return null;
              return (
                <div key={g}>
                  <div className="mb-2 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                    {GRUPPO_LABEL[g]}
                  </div>
                  <div className="space-y-2.5">
                    {campi.map((c) => (
                      <label key={c.chiave} className="block text-[12.5px]">
                        <span className="flex items-center gap-1.5 font-medium text-muted-foreground">
                          {c.label}
                          {c.condiviso && (
                            <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 text-[9.5px] font-semibold text-blue-700">
                              condiviso
                            </span>
                          )}
                        </span>
                        <input
                          type={c.tipo === "date" ? "date" : "text"}
                          value={valori[c.chiave] ?? ""}
                          disabled={c.readonly}
                          placeholder={c.in_attesa_di ? "arriverà con la sorgente" : ""}
                          onChange={(e) =>
                            setValori((v) => ({ ...v, [c.chiave]: e.target.value }))
                          }
                          className={cn(
                            "mt-1 w-full rounded-md border px-3 py-2 text-sm outline-none",
                            c.readonly
                              ? "border-border bg-muted text-muted-foreground"
                              : "border-border bg-card focus:border-brand-500",
                          )}
                        />
                        {c.nota && (
                          <span className="mt-0.5 block text-[10px] text-muted-foreground">
                            {c.nota}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="mt-3 border-t border-border pt-3">
          <Button
            className="w-full"
            disabled={salva.isPending || !data}
            onClick={() => {
              const editabili: Record<string, string> = {};
              for (const c of data?.campi ?? []) {
                if (!c.readonly) editabili[c.chiave] = valori[c.chiave] ?? "";
              }
              salva.mutate({ valori: editabili });
            }}
          >
            {salva.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 h-4 w-4" />
            )}
            Salva e rigenera anteprima
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Drawer ─────────────────────────────────────────────────────────────

export function PacchettoDrawer({
  caricoId,
  onClose,
}: {
  caricoId: string;
  onClose: () => void;
}) {
  const { data: pacco, isLoading } = useM3DocumentiPacchetto(caricoId);
  const [selDoc, setSelDoc] = useState<{ tipo: string; label: string } | null>(null);
  const [regen, setRegen] = useState<
    { scope: "pacchetto" } | { scope: "documento"; tipo: string; label: string } | null
  >(null);
  const [anteprima, setAnteprima] = useState<{ fileId: string; label: string } | null>(null);

  const inL2 = selDoc != null;

  return (
    <div className="fixed inset-0 top-14 z-40 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div
        className={cn(
          "relative z-10 flex h-full max-w-[97vw] flex-col border-l bg-background shadow-xl transition-[width] duration-200",
          inL2 ? "w-[1280px]" : "w-[860px]",
        )}
      >
        <header className="flex items-start gap-3 border-b border-border px-5 py-3">
          {inL2 && (
            <button
              onClick={() => setSelDoc(null)}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground"
              title="Torna alla panoramica"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[15px] font-bold">
              {inL2
                ? selDoc.label
                : pacco
                  ? `${pacco.numero_ndp ? `NDP ${pacco.numero_ndp} — ` : ""}${pacco.cliente_ragione_sociale}`
                  : "Pacchetto"}
            </h3>
            <div className="truncate text-[12px] text-muted-foreground">
              {inL2 ? (
                <span className="inline-flex items-center gap-1">
                  Completamento documento <ChevronRight className="h-3 w-3" />{" "}
                  {pacco?.cliente_ragione_sociale}
                </span>
              ) : pacco ? (
                `${pacco.paese_nome ?? pacco.paese_iso ?? "—"} · ${pacco.data ?? "—"}`
              ) : (
                ""
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {isLoading || !pacco ? (
          <div className="flex-1 space-y-3 p-5">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : inL2 ? (
          <Livello2 caricoId={caricoId} tipo={selDoc.tipo} />
        ) : (
          <Livello1
            pacco={pacco}
            onCompleta={(tipo) =>
              setSelDoc({
                tipo,
                label: pacco.documenti.find((d) => d.tipo === tipo)?.label ?? tipo,
              })
            }
            onRigeneraDoc={(tipo, label) => setRegen({ scope: "documento", tipo, label })}
            onAnteprima={(fileId, label) => setAnteprima({ fileId, label })}
          />
        )}

        {/* Footer */}
        {!inL2 && pacco && (
          <div className="flex items-center gap-2 border-t border-border p-3">
            <Button variant="outline" onClick={() => setRegen({ scope: "pacchetto" })}>
              <RefreshCw className="mr-1.5 h-4 w-4" /> Rigenera pacchetto
            </Button>
            <Button
              className="flex-1"
              onClick={() => apiDownloadFile(`/api/v1/m3/documenti/pacchetti/${caricoId}/zip`)}
            >
              <Download className="mr-1.5 h-4 w-4" /> Scarica tutto (zip)
            </Button>
          </div>
        )}
      </div>

      {regen && (
        <RigeneraDialog
          scope={regen.scope}
          caricoId={caricoId}
          tipo={regen.scope === "documento" ? regen.tipo : undefined}
          label={regen.scope === "documento" ? regen.label : undefined}
          onClose={() => setRegen(null)}
          onDone={() => setRegen(null)}
        />
      )}

      {anteprima && (
        <AnteprimaModal
          fileId={anteprima.fileId}
          label={anteprima.label}
          onClose={() => setAnteprima(null)}
        />
      )}
    </div>
  );
}

function AnteprimaModal({
  fileId,
  label,
  onClose,
}: {
  fileId: string;
  label: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex h-[88vh] w-[900px] max-w-[95vw] flex-col overflow-hidden rounded-lg bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <Eye className="h-4 w-4 text-brand-700" />
          <span className="truncate text-sm font-semibold">{label}</span>
          <button
            onClick={onClose}
            className="ml-auto rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground"
            title="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 bg-muted">
          <PdfPreview fileId={fileId} />
        </div>
      </div>
    </div>
  );
}
